const { Core, Utils } = Global;
const { $active } = Utils;

const HIDE_AT = 96;
const SCROLL_MAX_MS = 5000;
const SCROLL_STALL_MS = 450;

const getShowThreshold = () => Math.max(400, window.innerHeight * 0.45);

const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const getScrollY = () =>
    window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;

const withInstantScrollBehavior = (fn) => {
    const root = document.documentElement;
    const body = document.body;
    const prevRoot = root.style.scrollBehavior;
    const prevBody = body.style.scrollBehavior;

    root.style.scrollBehavior = 'auto';
    body.style.scrollBehavior = 'auto';

    try {
        fn();
    } finally {
        root.style.scrollBehavior = prevRoot;
        body.style.scrollBehavior = prevBody;
    }
};

customElements.define('scroll-to-top-button', class extends Core {
    elements = {
        $: ['button']
    };

    propTypes = {
        observe: Boolean
    };

    render() {
        this.$('button', {
            click: (event) => {
                event.preventDefault();
                this._handleClick();
            },
            keydown: this._handleKeydown
        });

        if (this.prop('observe')) {
            this._visible = false;
            this._scrollingToTop = false;
            this._scrollTicking = false;
            this._onScroll = () => this._scheduleVisibilitySync();
            window.addEventListener('scroll', this._onScroll, { passive: true });
            this._syncVisibility();
        }
    }

    disconnectedCallback() {
        if (this._onScroll) {
            window.removeEventListener('scroll', this._onScroll);
        }
        this._scrollingToTop = false;
    }

    _scheduleVisibilitySync() {
        if (this._scrollTicking) return;
        this._scrollTicking = true;
        requestAnimationFrame(() => {
            this._syncVisibility();
            this._scrollTicking = false;
        });
    }

    _syncVisibility() {
        if (this._scrollingToTop) {
            this.active = true;
            return;
        }

        const scrollY = getScrollY();
        const showThreshold = getShowThreshold();

        if (this._visible) {
            if (scrollY < HIDE_AT) {
                this._visible = false;
            }
        } else if (scrollY > showThreshold) {
            this._visible = true;
        }

        this.active = this._visible;
    }

    _scrollToTop(behavior = 'smooth') {
        if (behavior === 'auto') {
            withInstantScrollBehavior(() => {
                window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
                document.documentElement.scrollTop = 0;
                document.body.scrollTop = 0;
            });
            return;
        }

        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }

    _handleClick() {
        const startY = getScrollY();
        const useInstant = prefersReducedMotion() || (this._scrollingToTop && startY > HIDE_AT);

        this._scrollingToTop = true;
        this._visible = true;
        this.active = true;
        this._scrollStartY = startY;

        this._scrollToTop(useInstant ? 'auto' : 'smooth');
        this._watchScrollEnd();
    }

    _finishScrollToTop() {
        this._scrollingToTop = false;
        this._scrollStartY = 0;
        this._visible = false;
        this.active = false;
    }

    _watchScrollEnd() {
        const startedAt = Date.now();
        let lastY = getScrollY();
        let lastMovedAt = Date.now();
        let forcedInstant = false;

        const step = () => {
            if (!this._scrollingToTop) return;

            const scrollY = getScrollY();

            if (scrollY <= HIDE_AT) {
                this._finishScrollToTop();
                return;
            }

            if (scrollY < lastY - 2) {
                lastMovedAt = Date.now();
            }
            lastY = scrollY;

            const stalled = Date.now() - lastMovedAt >= SCROLL_STALL_MS;
            const timedOut = Date.now() - startedAt >= SCROLL_MAX_MS;

            if (!forcedInstant && (stalled || timedOut)) {
                forcedInstant = true;
                this._scrollToTop('auto');
                lastMovedAt = Date.now();
            }

            requestAnimationFrame(step);
        };

        requestAnimationFrame(step);
    }

    _handleKeydown(event) {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this._handleClick();
        }
    }

    set active(state) {
        const button = this.$('button');
        $active(button, state);

        if (!button) return;

        button.setAttribute('aria-hidden', state ? 'false' : 'true');
        button.tabIndex = state ? 0 : -1;
    }
});
